/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    Text,
    useDisclosure
} from '@chakra-ui/react'
import useDoubleOptIn from './use-double-opt-in'
import LoginForm from '../components/login'
import ResetPasswordForm from '../components/reset-password'
import RegisterForm from '../components/register'
import {noop} from '../utils/utils'
import useRegistrationErrorMessage from '../hooks/use-error-message'
import {useWishList, useMergeWishlistMutation} from '../hooks/use-wish-list'
import useOcapiSite from '../hooks/useOcapiSite'
import {
    ACCOUNT_HREF,
    API_ERROR_MESSAGE,
    HYBRID_ACCOUNT,
    HYBRID_AUTH,
    TOAST_MESSAGE_MERGE_WISHLIST
} from '../constants'
import {useToast} from './use-toast'
import useHybridURL from './use-hybrid-url'
import useEffectEvent from './use-effect-event'
import {trackEvent} from '../tracking/analytics'
import {
    AuthHelpers,
    ShopperCustomersMutations,
    useAuthHelper,
    useCustomer,
    useCustomerId,
    useCustomerType,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useOcapiPasswordResetMutation, useOcapiSubscribeToNewsletterMutation} from './useOcapi'
import HybridLink from '../components/hybrid-link'
const LOGIN_VIEW = 'login'
const REGISTER_VIEW = 'register'
const PASSWORD_VIEW = 'password'
import {useD2BCustomer} from './use-d2b-customer'
import {useCurrentBasket} from './use-current-basket'

export const AuthModal = ({
    initialView = LOGIN_VIEW,
    onLoginSuccess = noop,
    onRegistrationSuccess = noop,
    onPasswordResetSuccess = noop,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const {isDoubleOptInEnabled} = useDoubleOptIn()
    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()
    const errorMsg = useRegistrationErrorMessage()
    // TODO v3: check if this is needed
    // const prevAuthType = usePrevious(customerType)

    const customer = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && isRegistered}
    )

    const hybridURL = useHybridURL()
    const [currentView, setCurrentView] = useState(initialView)
    const form = useForm()
    const submittedEmail = useRef()
    const toast = useToast()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const registerWithLogin = useAuthHelper(AuthHelpers.Register)
    const registerWithoutLogin = useShopperCustomersMutation(
        ShopperCustomersMutations.RegisterCustomer
    )
    const {mutateAsync: subscribeToNewsletter} = useOcapiSubscribeToNewsletterMutation()
    const passwordReset = useOcapiPasswordResetMutation()
    const {wishlistEnabled} = useOcapiSite().preferences
    const isD2BEnabled = useD2BCustomer().enabled
    const {data: basket} = useCurrentBasket()

    const {mutate: mergeWishlistMutation} = useMergeWishlistMutation(
        {enabled: !!wishlistEnabled},
        {
            onSettled: (data) => {
                if (data) {
                    toast({
                        title: `${formatMessage(TOAST_MESSAGE_MERGE_WISHLIST)}`
                    })
                }
            }
        }
    )
    const {data: wishlist} = useWishList({enabled: !!wishlistEnabled})

    const wishlistProductIds = useMemo(
        () =>
            wishlistEnabled
                ? wishlist?.customerProductListItems?.map((item) => item.productId)
                : [],
        [wishlistEnabled, wishlist?.customerProductListItems]
    )

    // TODO v3: check if this is needed
    // const {data: baskets} = useCustomerBaskets(
    //   {parameters: {customerId}},
    //   {enabled: !!customerId && !isServer, keepPreviousData: true}
    // )
    // const mergeBasket = useShopperBasketsMutation('mergeBasket')

    const submitForm = async (data) => {
        form.clearErrors()

        return {
            login: async (data) => {
                const loginPromise = login.mutateAsync({
                    username: data.email,
                    password: data.password
                })
                const trackEventPromise = trackEvent({event: 'login', email: data.email})
                // wait for tracking and login to complete, as otherwise the user might be redirected before the tracking is sent
                await Promise.allSettled([loginPromise, trackEventPromise])

                // TODO v3: check if this is needed
                // const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
                // // we only want to merge basket when the user is logged in as a recurring user
                // // only recurring users trigger the login mutation, new user triggers register mutation
                // // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
                // // if you change logic here, also change it in login page
                // const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'
                // if (shouldMergeBasket) {
                //     mergeBasket.mutate({
                //         headers: {
                //             // This is not required since the request has no body
                //             // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                //             'Content-Type': 'application/json'
                //         },
                //         parameters: {
                //             createDestinationBasket: true
                //         }
                //     })
                // }

                if (wishlistEnabled) {
                    mergeWishlistMutation(wishlistProductIds)
                }

                loginPromise.catch((data) => {
                    form.setError('global', {type: 'manual', message: ''})
                    data.response.json().then((error) => {
                        const message = /account.user.registration.activationmessage/i.test(
                            error.message
                        )
                            ? formatMessage({
                                  defaultMessage:
                                      'In order to login, you have to activate your account from the link we sent you in an email.',
                                  id: 'account-registration.msg.double_opt_in'
                              })
                            : formatMessage({
                                  defaultMessage:
                                      'Incorrect username or password, please try again.',
                                  id: 'login_page.error.incorrect_username_or_password'
                              })
                        form.setError('global', {type: 'manual', message})
                    })
                })
            },
            register: async (data) => {
                const body = {
                    customer: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        email: data.email,
                        login: data.email
                    },
                    password: data.password
                }

                // intentionally not awaiting the promise here as we want to run in parallel with the newsletter subscription and tracking
                const registerPromise = !isDoubleOptInEnabled(data.email)
                    ? registerWithLogin.mutateAsync(body)
                    : registerWithoutLogin.mutateAsync({body})

                // intentionally not awaiting the promise here as we want to run in parallel with the registration and tracking
                const newsletterPromise = data.newsletter
                    ? subscribeToNewsletter({
                          parameters: {
                              email: data.email,
                              firstName: data.firstName,
                              lastName: data.lastName
                          }
                      })
                    : true

                // intentionally not awaiting the promise here as we want to run in parallel with the registration and newsletter subscription
                const trackEventPromise = trackEvent({
                    event: 'account_create',
                    email: data.email
                })

                // wait for tracking and registration to complete, as otherwise the user might be redirected before the tracking is sent
                const [registerOutcome] = await Promise.allSettled([
                    registerPromise,
                    trackEventPromise,
                    newsletterPromise
                ])

                if (registerOutcome.status !== 'fulfilled') {
                    const message = await errorMsg(registerOutcome.reason)
                    form.setError('global', {type: 'manual', message})
                    return
                }

                if (!isDoubleOptInEnabled(data.email)) {
                    hybridURL(HYBRID_ACCOUNT, ACCOUNT_HREF)
                } else {
                    toast({
                        title: `${formatMessage({
                            defaultMessage:
                                'In order to login, you have to activate your account from the link we sent you in an email.',
                            id: 'account-registration.msg.double_opt_in'
                        })}`
                    })
                    props.onClose()
                }
            },
            password: async (data) => {
                try {
                    const body = {
                        identification: data.email,
                        type: 'email'
                    }
                    await passwordReset.mutateAsync({body})

                    // Execute action to be perfromed on successful passoword reset
                    await onPasswordResetSuccess?.()
                    submittedEmail.current = data.email
                } catch (error) {
                    console.error('Error resetting password', error)
                    form.setError('global', {
                        type: 'manual',
                        message: formatMessage(API_ERROR_MESSAGE)
                    })
                }
            }
        }[currentView](data)
    }

    // Reset form and local state when opening the modal
    const onOpen = useEffectEvent(() => {
        setCurrentView(initialView)
        submittedEmail.current = undefined
        form.reset()
    })
    useEffect(() => {
        if (props.isOpen) {
            onOpen()
        }
    }, [props.isOpen, onOpen])

    // Auto-focus the first field in each form view
    const fieldsRef = form.control?.fieldsRef?.current
    useEffect(() => {
        if (fieldsRef) {
            const initialField = {
                [LOGIN_VIEW]: 'email',
                [REGISTER_VIEW]: 'firstName',
                [PASSWORD_VIEW]: 'email'
            }[currentView]
            fieldsRef?.[initialField]?.ref.focus()
        }
    }, [currentView, fieldsRef])

    // Clear form state when changing views
    const onViewChange = useEffectEvent(() => {
        form.reset()
    })
    useEffect(() => {
        onViewChange(currentView)
    }, [currentView, onViewChange])

    const onCustomerChange = useEffectEvent((customer) => {
        // Lets determine if the user has either logged in, or registed.
        const loggingIn = currentView === LOGIN_VIEW
        const registering = currentView === REGISTER_VIEW
        const isNowRegistered = props.isOpen && isRegistered && (loggingIn || registering)
        // If the customer changed, but it's not because they logged in or registered. Do nothing.
        if (!isNowRegistered) {
            return
        }

        // We are done with the modal.
        props.onClose()

        if (isDoubleOptInEnabled(customer.email) && !customer.c_activated) {
            toast({
                title: formatMessage({
                    defaultMessage:
                        'In order to login, you have to activate your account from the link we sent you in an email.',
                    id: 'account-registration.msg.double_opt_in'
                })
            })
            return
        }

        // Show a toast only for those registered users returning to the site.
        if (loggingIn) {
            toast({
                title: `${formatMessage(
                    {
                        defaultMessage: 'Hello {name}, welcome!',
                        id: 'auth_modal.info.welcome_user'
                    },
                    {
                        name: customer.firstName
                    }
                )} ${formatMessage({
                    defaultMessage: "You're now signed in.",
                    id: 'auth_modal.description.now_signed_in'
                })}`
            })

            // Execute action to be performed on successful login
            onLoginSuccess()
        }

        if (registering) {
            // Execute action to be performed on successful registration
            onRegistrationSuccess()
        }

        // Show second toast for D2B customers
        if (isD2BEnabled && basket?.productItems?.length > 0) {
            toast({
                title: `${formatMessage({
                    defaultMessage:
                        'Please review your order summary as member discount has been applied.',
                    id: 'd2b.msg.review_order_total'
                })}`
            })
        }
    })
    useEffect(() => {
        if (customer.data && !customer.isLoading) {
            onCustomerChange(customer.data)
        }
    }, [isRegistered, customer.data, customer.isLoading, onCustomerChange])

    const onBackToSignInClick = () =>
        initialView === PASSWORD_VIEW ? props.onClose() : setCurrentView(LOGIN_VIEW)

    const PasswordResetSuccess = () => (
        <Stack spacing={8}>
            <Text color="stokkePink.dark" textStyle="Regular 45XLarge">
                <FormattedMessage
                    defaultMessage={'Password Reset'}
                    id="auth_modal.password_reset_success.title.password_reset"
                />
            </Text>
            <Text textStyle="Regular Medium">
                <FormattedMessage
                    defaultMessage="If you have an account with this email address <b>{email}</b>, please check your inbox or spam folder. If you do not see a password reset email, please check if you have entered your email address correctly or <account>create an account</account>."
                    id="reset_password.info.receive_email"
                    values={{
                        email: submittedEmail.current,
                        b: (chunks) => <b>{chunks}</b>,
                        account: (chunks) => (
                            <HybridLink
                                page={HYBRID_AUTH}
                                href="/registration"
                                hybridHref="/registration"
                                size="md"
                            >
                                {chunks}
                            </HybridLink>
                        )
                    }}
                />
            </Text>

            <Button onClick={onBackToSignInClick}>
                <FormattedMessage
                    defaultMessage="Back to Sign In"
                    id="auth_modal.password_reset_success.button.back_to_sign_in"
                />
            </Button>
        </Stack>
    )

    return (
        <Modal
            variant="auth"
            closeOnOverlayClick={false}
            returnFocusOnClose={false}
            data-testid="sf-auth-modal"
            {...props}
        >
            <ModalOverlay />
            <ModalContent
                aria-label={formatMessage({
                    id: 'login.page.title',
                    defaultMessage: 'Login into your Stokke® Account | Stokke® Online Shop'
                })}
            >
                <ModalCloseButton />
                <ModalBody>
                    {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                        <LoginForm
                            form={form}
                            submitForm={submitForm}
                            clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                            clickForgotPassword={() => setCurrentView(PASSWORD_VIEW)}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === REGISTER_VIEW && (
                        <RegisterForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                        <ResetPasswordForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                        <PasswordResetSuccess />
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

AuthModal.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW]),
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    onRegistrationSuccess: PropTypes.func,
    onPasswordResetSuccess: PropTypes.func
}

/**
 *
 * @param {('register'|'login'|'password')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (initialView = LOGIN_VIEW) => {
    const {isOpen, onOpen, onClose} = useDisclosure()

    return {
        initialView,
        isOpen,
        onOpen,
        onClose
    }
}
